import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

// Public share API (no auth header) - used for /api/public/share/* endpoints
const publicShareApi = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL || '',
});

/**
 * Fetch time entries with server-side pagination/search/filters.
 * Pass page, page_size, search, project_id, user_id, dates, is_manual, flagged_only, scope, etc.
 */
export const useTimeEntries = (filters = {}) => {
  return useQuery({
    queryKey: ['timeEntries', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters || {}).forEach(([key, value]) => {
        if (value !== '' && value != null && value !== false) {
          params.append(key, String(value));
        }
      });
      const response = await axios.get(`/api/time-entries?${params}`);
      return response.data;
    },
    staleTime: 30 * 1000,
    placeholderData: (previousData) => previousData,
  });
};

/** Summary cards for Time Entries (full counts, not page-capped). */
export const useTimeEntryStatistics = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ['timeEntryStatistics', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters || {}).forEach(([key, value]) => {
        if (value !== '' && value != null && value !== false) {
          params.append(key, String(value));
        }
      });
      const response = await axios.get(`/api/time-entries/statistics?${params}`);
      return response.data;
    },
    staleTime: 30 * 1000,
    enabled: options.enabled !== false,
  });
};

// Projects Hook
export const useProjects = () => {
  return useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const response = await axios.get('/api/projects/?page_size=1000');
      return response.data;
    },
    staleTime: 30 * 1000, // 30s — lists should refresh promptly after create/update
  });
};

// Tasks Hook
/**
 * Fetch tasks with server-side pagination/search/filters.
 * Accepts either a params object or a legacy projectId string.
 */
export const useTasks = (paramsOrProjectId = {}) => {
  const params =
    typeof paramsOrProjectId === 'string' || paramsOrProjectId == null
      ? {
          ...(paramsOrProjectId ? { project_id: paramsOrProjectId } : {}),
          // Legacy callers (e.g. TimeEntries dropdown) need a fuller list for one project
          page_size: 200,
        }
      : paramsOrProjectId;

  return useQuery({
    queryKey: ['tasks', params],
    queryFn: async () => {
      const qs = new URLSearchParams();
      Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== '' && value != null) qs.set(key, String(value));
      });
      const response = await axios.get(`/api/tasks/?${qs}`);
      return response.data;
    },
    staleTime: 30 * 1000,
    placeholderData: (previousData) => previousData,
  });
};

// Sprints Hook - fetch sprints for a project
export const useSprints = (projectId) => {
  return useQuery({
    queryKey: ['sprints', projectId],
    queryFn: async () => {
      const response = await axios.get('/api/sprints', {
        params: { project_id: projectId },
      });
      return response.data;
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useCreateSprint = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await axios.post('/api/sprints', data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sprints', variables.project_id] });
      queryClient.invalidateQueries({ queryKey: ['projectDetail', variables.project_id] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};

export const useUpdateSprint = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ sprintId, projectId, data }) => {
      const response = await axios.put(`/api/sprints/${sprintId}`, data);
      return response.data;
    },
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ['sprints', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projectDetail', projectId] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};

export const useDeleteSprint = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (sprintId) => {
      const response = await axios.delete(`/api/sprints/${sprintId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sprints'] });
      queryClient.invalidateQueries({ queryKey: ['projectDetail'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};

// Users Hook - includes admins so they can be assigned to projects/tasks etc.
export const useUsers = () => {
  return useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await axios.get('/api/users/all', { params: { include_admins: true } });
      return response.data;
    },
    staleTime: 15 * 60 * 1000, // 15 minutes - users change rarely
  });
};

// Mutations with automatic cache invalidation
export const useCreateTimeEntry = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data) => {
      const response = await axios.post('/api/time-entries', data);
      return response.data;
    },
    onSuccess: () => {
      // Automatically refetch time entries and streak after creating
      queryClient.invalidateQueries({ queryKey: ['timeEntries'] });
      queryClient.invalidateQueries({ queryKey: ['timeEntryStatistics'] });
      queryClient.invalidateQueries({ queryKey: ['userStreak'] });
      queryClient.invalidateQueries({ queryKey: ['dailyHours'] });
    },
  });
};

export const useUpdateTimeEntry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await axios.put(`/api/time-entries/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timeEntries'] });
      queryClient.invalidateQueries({ queryKey: ['timeEntryStatistics'] });
      queryClient.invalidateQueries({ queryKey: ['userStreak'] });
      queryClient.invalidateQueries({ queryKey: ['dailyHours'] });
    },
  });
};

export const useDeleteTimeEntry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await axios.delete(`/api/time-entries/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timeEntries'] });
      queryClient.invalidateQueries({ queryKey: ['timeEntryStatistics'] });
      queryClient.invalidateQueries({ queryKey: ['userStreak'] });
      queryClient.invalidateQueries({ queryKey: ['dailyHours'] });
    },
  });
};

// Project Detail Hook
export const useProjectDetail = (projectId) => {
  return useQuery({
    queryKey: ['projectDetail', projectId],
    queryFn: async () => {
      const response = await axios.get(`/api/projects/${projectId}/details`);
      return response.data;
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Task Statuses Hook
export const useTaskStatuses = () => {
  return useQuery({
    queryKey: ['taskStatuses'],
    queryFn: async () => {
      const response = await axios.get('/api/tasks/statuses');
      return response.data;
    },
    staleTime: 15 * 60 * 1000, // 15 minutes - statuses rarely change
  });
};

// User Detail Hook (admin-only endpoint — callers must gate with options.enabled)
export const useUserDetail = (userId, options = {}) => {
  return useQuery({
    queryKey: ['userDetail', userId],
    queryFn: async () => {
      const response = await axios.get(`/api/users/${userId}`);
      return response.data;
    },
    enabled: !!userId && options.enabled !== false,
    staleTime: 5 * 60 * 1000,
  });
};

// User Analytics Hook
export const useUserAnalytics = (userId, options = {}) => {
  const period = options.period;
  return useQuery({
    queryKey: ['userAnalytics', userId, period],
    queryFn: async () => {
      const params = period ? `?period=${period}` : '';
      const response = await axios.get(`/api/analytics/user-details/${userId}${params}`);
      return response.data;
    },
    enabled: !!userId && options.enabled !== false,
    staleTime: 5 * 60 * 1000,
  });
};

// Weekly 40h compliance history for a user (User Detail Analytics)
export const useWeeklyCompliance = (userId, weeks = 4, options = {}) => {
  return useQuery({
    queryKey: ['weeklyCompliance', userId, weeks],
    queryFn: async () => {
      const response = await axios.get(
        `/api/analytics/weekly-compliance/${userId}?weeks=${weeks}`
      );
      return response.data;
    },
    enabled: !!userId && options.enabled !== false,
    staleTime: 5 * 60 * 1000,
  });
};

// Admin action alerts for under-40h / chronic under-hours patterns
export const useActionAlerts = (weeks = 4, enabled = true) => {
  return useQuery({
    queryKey: ['actionAlerts', weeks],
    queryFn: async () => {
      const response = await axios.get(`/api/analytics/action-alerts?weeks=${weeks}`);
      return response.data;
    },
    enabled: enabled,
    staleTime: 5 * 60 * 1000,
  });
};

// User Projects Hook
export const useUserProjects = (userId, options = {}) => {
  return useQuery({
    queryKey: ['userProjects', userId],
    queryFn: async () => {
      const response = await axios.get(`/api/projects?assigned_to=${userId}`);
      return response.data;
    },
    enabled: !!userId && options.enabled !== false,
    staleTime: 5 * 60 * 1000,
  });
};

// User Tasks Hook
export const useUserTasks = (userId, options = {}) => {
  return useQuery({
    queryKey: ['userTasks', userId],
    queryFn: async () => {
      const response = await axios.get(`/api/tasks?assigned_to=${userId}`);
      return response.data;
    },
    enabled: !!userId && options.enabled !== false,
    staleTime: 5 * 60 * 1000,
  });
};

// User Time Entries Hook (server-paginated; use response.total for full count)
export const useUserTimeEntries = (userId, filters = {}, options = {}) => {
  return useQuery({
    queryKey: ['userTimeEntries', userId, filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('user_id', userId);
      Object.entries(filters || {}).forEach(([key, value]) => {
        if (value !== '' && value != null && value !== false) {
          params.append(key, String(value));
        }
      });
      if (!params.has('page_size')) {
        params.append('page_size', '50');
      }
      const response = await axios.get(`/api/time-entries?${params}`);
      return response.data;
    },
    enabled: !!userId && options.enabled !== false,
    staleTime: 2 * 60 * 1000,
    placeholderData: (previousData) => previousData,
  });
};

// Analytics Hook
export const useAnalytics = (period, customRange = null) => {
  return useQuery({
    queryKey: ['analytics', period, customRange],
    queryFn: async () => {
      let url = `/api/analytics/?period=${period}`;
      if (period === 'custom' && customRange) {
        url += `&start_date=${customRange.startDate}&end_date=${customRange.endDate}`;
      }
      const response = await axios.get(url);
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
};

// Analytics trends (hours by day, idle, utilization) for admin dashboard charts
export const useAnalyticsTrends = (period, customRange = null, enabled = true) => {
  return useQuery({
    queryKey: ['analyticsTrends', period, customRange],
    queryFn: async () => {
      let url = `/api/analytics/trends?period=${period}`;
      if (period === 'custom' && customRange) {
        url += `&start_date=${customRange.startDate}&end_date=${customRange.endDate}`;
      }
      const response = await axios.get(url);
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!period && enabled,
  });
};

// Documentation Hook
export const useDocumentation = (projectId, searchTerm = '') => {
  return useQuery({
    queryKey: ['documentation', projectId, searchTerm],
    queryFn: async () => {
      const params = { project_id: projectId, page_size: 50 };
      if (searchTerm) params.search = searchTerm;
      const response = await axios.get('/api/documentation/', { params });
      return response.data;
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
  });
};

// Calendar Status Hook
export const useCalendarStatus = () => {
  return useQuery({
    queryKey: ['calendarStatus'],
    queryFn: async () => {
      const response = await axios.get('/api/calendar/status');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
};

// Upcoming Meetings Hook — only call when Outlook is connected (avoids 401 spam)
export const useUpcomingMeetings = (options = {}) => {
  return useQuery({
    queryKey: ['upcomingMeetings'],
    queryFn: async () => {
      const response = await axios.get('/api/calendar/upcoming');
      // API returns CalendarEventsListResponse: { events, total_count, next_link }
      return response.data?.events ?? [];
    },
    enabled: options.enabled !== false,
    staleTime: 2 * 60 * 1000, // 2 minutes - meetings change frequently
    retry: false, // 401 "not connected" is expected when gated incorrectly; don't burst-retry
  });
};

// Assigned Tasks Hook
export const useAssignedTasks = () => {
  return useQuery({
    queryKey: ['assignedTasks', { scope: 'mine' }],
    queryFn: async () => {
      // Always scope to the current user's assignments. Project leads otherwise
      // receive every task in their projects (paginated), so their own assigned
      // work is often missing from the first page and the dashboard shows 0.
      const response = await axios.get('/api/tasks/', {
        params: { scope: 'mine', page_size: 200 },
      });
      return response.data;
    },
    staleTime: 2 * 60 * 1000,
  });
};

// Recent Activity Hook
export const useRecentActivity = () => {
  return useQuery({
    queryKey: ['recentActivity'],
    queryFn: async () => {
      const response = await axios.get('/api/activity-logs/my-activity?limit=10');
      return response.data;
    },
    staleTime: 1 * 60 * 1000, // 1 minute - activity is very dynamic
  });
};

// Notes Stats Hook
export const useNotesStats = () => {
  return useQuery({
    queryKey: ['notesStats'],
    queryFn: async () => {
      const response = await axios.get('/api/notes/dashboard/stats');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
};

// Leave Balance Hook
export const useLeaveBalance = () => {
  return useQuery({
    queryKey: ['leaveBalance'],
    queryFn: async () => {
      const response = await axios.get('/api/leaves/balance');
      return response.data;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Current Leave Hook
export const useCurrentLeave = (user) => {
  return useQuery({
    queryKey: ['currentLeave'],
    queryFn: async () => {
      const response = await axios.get('/api/leaves/my-leaves');
      return response.data;
    },
    enabled: user && (user.role === 'member' || user.role === 'project_lead'), // Only enable for members and project leads
    staleTime: 5 * 60 * 1000,
  });
};

// Member Stats Hook
export const useMemberStats = (period, options = {}) => {
  return useQuery({
    queryKey: ['memberStats', period],
    queryFn: async () => {
      const response = await axios.get(`/api/analytics/member-stats?period=${period}`);
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!period && options.enabled !== false,
  });
};

// Project Mutations
export const useCreateProject = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data) => {
      const response = await axios.post('/api/projects', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['projectStats'] });
    },
  });
};

export const useUpdateProject = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await axios.put(`/api/projects/${id}`, data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['projectDetail', id] });
    },
  });
};

// Scope-to-Tasks (AI Task Generator)
const SCOPE_ASYNC_THRESHOLD = 2 * 1024 * 1024; // 2MB

export const useScopeAnalyze = (projectId) => {
  return useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`/api/projects/${projectId}/scope/analyze`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
  });
};

export const useScopeAnalyzeAsync = (projectId) => {
  return useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`/api/projects/${projectId}/scope/analyze-async`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
  });
};

export const useScopeAnalyzeStatus = (projectId, taskId, options = {}) => {
  return useQuery({
    queryKey: ['scopeAnalyzeStatus', projectId, taskId],
    queryFn: async () => {
      const response = await axios.get(`/api/projects/${projectId}/scope/analyze-status/${taskId}`);
      return response.data;
    },
    enabled: !!projectId && !!taskId && (options.enabled !== false),
    // Poll every 2s while in progress; stop once terminal
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'SUCCESS' || status === 'FAILURE' || status === 'REVOKED') {
        return false;
      }
      return 2000;
    },
    staleTime: 0,
  });
};

export { SCOPE_ASYNC_THRESHOLD };

export const useScopeApply = (projectId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await axios.post(`/api/projects/${projectId}/scope/apply`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projectDetail', projectId] });
      queryClient.invalidateQueries({ queryKey: ['tasks', projectId] });
      queryClient.invalidateQueries({ queryKey: ['sprints', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
};

// Task Mutations
export const useCreateTask = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data) => {
      const response = await axios.post('/api/tasks', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['assignedTasks'] });
      queryClient.invalidateQueries({ queryKey: ['taskStatistics'] });
      queryClient.invalidateQueries({ queryKey: ['projectStats'] });
      queryClient.invalidateQueries({ queryKey: ['projectDetail'] });
    },
  });
};

export const useUpdateTask = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await axios.put(`/api/tasks/${id}`, data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['assignedTasks'] });
      queryClient.invalidateQueries({ queryKey: ['taskDetail', id] });
      // Invalidate all projectDetail queries to refresh task data
      queryClient.invalidateQueries({ queryKey: ['projectDetail'] });
    },
  });
};

/** Lighter mutation for task detail page inline edits - only invalidates taskDetail, uses optimistic updates for instant UI */
export const useUpdateTaskDetail = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await axios.put(`/api/tasks/${id}`, data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      // Only refetch task detail - no tasks list, assignedTasks, or projectDetail
      queryClient.invalidateQueries({ queryKey: ['taskDetail', id] });
    },
  });
};

// Task Detail Hook
export const useTaskDetail = (taskId) => {
  return useQuery({
    queryKey: ['taskDetail', taskId],
    queryFn: async () => {
      const response = await axios.get(`/api/tasks/${taskId}/details`);
      return response.data;
    },
    enabled: !!taskId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// Additional hooks for missing functionality
export const useDailyHours = () => {
  return useQuery({
    queryKey: ['dailyHours'],
    queryFn: async () => {
      const response = await axios.get('/api/time-entries/daily-hours');
      return response.data;
    },
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

export const useUserStreak = () => {
  return useQuery({
    queryKey: ['userStreak'],
    queryFn: async () => {
      const response = await axios.get('/api/time-entries/streak');
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useProjectStats = () => {
  return useQuery({
    queryKey: ['projectStats'],
    queryFn: async () => {
      const response = await axios.get('/api/projects/stats');
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useDeletedProjects = (options = {}) => {
  return useQuery({
    queryKey: ['deletedProjects'],
    queryFn: async () => {
      const response = await axios.get('/api/projects/deleted');
      return response.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: options.enabled !== false,
  });
};

export const useTaskStatistics = () => {
  return useQuery({
    queryKey: ['taskStatistics'],
    queryFn: async () => {
      const response = await axios.get('/api/tasks/statistics');
      return response.data;
    },
    staleTime: 30 * 1000, // 30s — keep summary cards in sync after create/update
  });
};

export const useDeletedTasks = (options = {}) => {
  return useQuery({
    queryKey: ['deletedTasks'],
    queryFn: async () => {
      const response = await axios.get('/api/tasks/deleted');
      return response.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: options.enabled !== false,
  });
};

export const useTeamActive = (options = {}) => {
  return useQuery({
    queryKey: ['teamActive'],
    queryFn: async () => {
      const response = await axios.get('/api/team/active');
      return response.data;
    },
    staleTime: 30 * 1000, // 30s — keep in sync with team member changes
    enabled: options.enabled !== false,
  });
};

// Mutations for CRUD operations

export const useDeleteProject = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id) => {
      const response = await axios.delete(`/api/projects/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['projectStats'] });
    },
  });
};

export const useRestoreProject = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id) => {
      const response = await axios.post(`/api/projects/${id}/restore`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['deletedProjects'] });
      queryClient.invalidateQueries({ queryKey: ['projectStats'] });
    },
  });
};

export const useDeleteTask = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id) => {
      const response = await axios.delete(`/api/tasks/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['assignedTasks'] });
      queryClient.invalidateQueries({ queryKey: ['taskStatistics'] });
    },
  });
};

export const useRestoreTask = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id) => {
      const response = await axios.post(`/api/tasks/${id}/restore`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['deletedTasks'] });
      queryClient.invalidateQueries({ queryKey: ['taskStatistics'] });
    },
  });
};

export const useCreateTaskStatus = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data) => {
      const response = await axios.post('/api/tasks/statuses', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taskStatuses'] });
    },
  });
};

export const useDeleteTaskStatus = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id) => {
      const response = await axios.delete(`/api/tasks/statuses/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taskStatuses'] });
    },
  });
};

// Additional hooks for all remaining functionality

// ---------------------------------------------------------------------------
// Notifications (header bell)
// ---------------------------------------------------------------------------

export const NOTIFICATION_PAGE_SIZE = 20;

export const notificationKeys = {
  all: ['notifications'],
  list: (limit) => ['notifications', 'list', limit],
  feeds: () => ['notifications', 'feed'],
  feed: (filters) => ['notifications', 'feed', filters],
  unreadCount: () => ['notifications', 'unreadCount'],
};

export const isInboxNotification = (notification) =>
  notification?.source === 'admin' || notification?.type === 'personal';

const invalidateInboxQueries = (queryClient) => {
  queryClient.invalidateQueries({ queryKey: ['inboxItems'] });
  queryClient.invalidateQueries({ queryKey: ['inboxUnreadCount'] });
};

const fetchNotificationFeedPage = async ({ unreadOnly, search }, cursor, signal) => {
  const params = { limit: NOTIFICATION_PAGE_SIZE };
  if (unreadOnly) params.unread_only = true;
  if (search) params.search = search;
  if (cursor) params.cursor = cursor;
  const response = await axios.get('/api/notifications/feed', { params, signal });
  return response.data;
};

const notificationFeedOptions = (filters) => ({
  queryKey: notificationKeys.feed(filters),
  queryFn: ({ pageParam, signal }) => fetchNotificationFeedPage(filters, pageParam, signal),
  initialPageParam: null,
  getNextPageParam: (lastPage) => lastPage?.next_cursor || undefined,
  staleTime: 30 * 1000,
});

/** Latest notifications as a flat list (Profile tab). */
export const useNotifications = (limit = 20) => {
  return useQuery({
    queryKey: notificationKeys.list(limit),
    queryFn: async () => {
      const response = await axios.get('/api/notifications/', { params: { limit } });
      return response.data;
    },
    staleTime: 2 * 60 * 1000,
  });
};

/** Cursor-paginated bell feed; keeps the previous results visible while a new search loads. */
export const useNotificationFeed = ({ unreadOnly = false, search = '' } = {}, { enabled = true } = {}) => {
  const filters = { unreadOnly, search: search.trim() };
  return useInfiniteQuery({
    ...notificationFeedOptions(filters),
    enabled,
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey?.[2]?.unreadOnly === unreadOnly ? previous : undefined,
  });
};

export const prefetchNotificationFeed = (queryClient) =>
  queryClient.prefetchInfiniteQuery(notificationFeedOptions({ unreadOnly: false, search: '' }));

export const useNotificationUnreadCount = () => {
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: async () => {
      const response = await axios.get('/api/notifications/unread-count');
      return response.data;
    },
    staleTime: 15 * 1000,
    // Fallback for writers that don't push over the socket (e.g. leave updates).
    refetchInterval: 60 * 1000,
    refetchOnWindowFocus: true,
  });
};

const updateCachedFeeds = (queryClient, updateItems) => {
  queryClient.setQueriesData({ queryKey: notificationKeys.feeds() }, (data) => {
    if (!data?.pages) return data;
    return {
      ...data,
      pages: data.pages.map((page) => ({ ...page, items: updateItems(page.items || []) })),
    };
  });
};

const adjustCachedUnreadCount = (queryClient, update) => {
  queryClient.setQueryData(notificationKeys.unreadCount(), (old) =>
    old ? { ...old, unread_count: Math.max(0, update(old.unread_count || 0)) } : old
  );
};

const snapshotNotificationQueries = async (queryClient) => {
  await queryClient.cancelQueries({ queryKey: notificationKeys.all });
  return queryClient.getQueriesData({ queryKey: notificationKeys.all });
};

const restoreNotificationQueries = (queryClient, context) => {
  (context?.snapshot || []).forEach(([key, data]) => queryClient.setQueryData(key, data));
};

export const useMarkNotificationRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [...notificationKeys.all, 'markRead'],
    mutationFn: async (notification) => {
      const response = await axios.put(`/api/notifications/${notification.id}/read`);
      return response.data;
    },
    onMutate: async (notification) => {
      const snapshot = await snapshotNotificationQueries(queryClient);
      updateCachedFeeds(queryClient, (items) =>
        items.map((n) => (n.id === notification.id ? { ...n, is_read: true } : n))
      );
      if (!notification.is_read) adjustCachedUnreadCount(queryClient, (count) => count - 1);
      return { snapshot };
    },
    onError: (_error, _notification, context) => restoreNotificationQueries(queryClient, context),
    onSettled: (_data, _error, notification) => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
      if (isInboxNotification(notification)) invalidateInboxQueries(queryClient);
    },
  });
};

export const useMarkAllNotificationsRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [...notificationKeys.all, 'markAllRead'],
    mutationFn: async () => {
      const response = await axios.post('/api/notifications/mark-read');
      return response.data;
    },
    onMutate: async () => {
      const snapshot = await snapshotNotificationQueries(queryClient);
      updateCachedFeeds(queryClient, (items) =>
        items.map((n) => (n.is_read ? n : { ...n, is_read: true }))
      );
      adjustCachedUnreadCount(queryClient, () => 0);
      return { snapshot };
    },
    onError: (_error, _vars, context) => restoreNotificationQueries(queryClient, context),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
      invalidateInboxQueries(queryClient);
    },
  });
};

export const useDismissNotification = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [...notificationKeys.all, 'dismiss'],
    mutationFn: async (notification) => {
      const response = await axios.delete(`/api/notifications/${notification.id}`);
      return response.data;
    },
    onMutate: async (notification) => {
      const snapshot = await snapshotNotificationQueries(queryClient);
      updateCachedFeeds(queryClient, (items) => items.filter((n) => n.id !== notification.id));
      if (!notification.is_read) adjustCachedUnreadCount(queryClient, (count) => count - 1);
      return { snapshot };
    },
    onError: (_error, _notification, context) => restoreNotificationQueries(queryClient, context),
    onSettled: (_data, _error, notification) => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
      if (isInboxNotification(notification)) invalidateInboxQueries(queryClient);
    },
  });
};

export const useSystemSettings = () => {
  return useQuery({
    queryKey: ['systemSettings'],
    queryFn: async () => {
      const { mergeSystemSettings } = await import('../constants/systemSettingsDefaults');
      // Trailing slash avoids FastAPI 307 redirect (proxy/clients can drop the body)
      const response = await axios.get('/api/system-settings/');
      return mergeSystemSettings(response.data);
    },
    staleTime: 60 * 1000,
    refetchOnMount: 'always',
  });
};

export const useIntegrationStatus = () => {
  return useQuery({
    queryKey: ['integrationStatus'],
    queryFn: async () => {
      const response = await axios.get('/api/integrations/status');
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useGitHubConnectionStatus = () => {
  return useQuery({
    queryKey: ['githubConnectionStatus'],
    queryFn: async () => {
      const response = await axios.get('/api/github/connection-status');
      return response.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useGitHubOrganizations = () => {
  return useQuery({
    queryKey: ['githubOrganizations'],
    queryFn: async () => {
      const response = await axios.get('/api/github/organizations');
      return response.data;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useGitHubRepositories = () => {
  return useQuery({
    queryKey: ['githubRepositories'],
    queryFn: async () => {
      const response = await axios.get('/api/github/repositories');
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useRepositoryCommits = (repository, taskId) => {
  return useQuery({
    queryKey: ['repositoryCommits', repository, taskId],
    queryFn: async () => {
      const response = await axios.get(`/api/github/repositories/${repository}/commits?task_id=${taskId}`);
      return response.data;
    },
    enabled: !!repository && !!taskId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useTaskActivity = (taskId, offset = 0) => {
  return useQuery({
    queryKey: ['taskActivity', taskId, offset],
    queryFn: async () => {
      const response = await axios.get(`/api/tasks/${taskId}/activity?offset=${offset}`);
      return response.data;
    },
    enabled: !!taskId,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

export const useTaskSubtasks = (taskId) => {
  return useQuery({
    queryKey: ['taskSubtasks', taskId],
    queryFn: async () => {
      const response = await axios.get(`/api/tasks/${taskId}/subtasks`);
      return response.data;
    },
    enabled: !!taskId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useTaskNotes = (taskId) => {
  return useQuery({
    queryKey: ['taskNotes', taskId],
    queryFn: async () => {
      const response = await axios.get(`/api/tasks/${taskId}/notes`);
      return response.data;
    },
    enabled: !!taskId,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

export const useDailyHoursForTask = (taskId) => {
  return useQuery({
    queryKey: ['dailyHoursForTask', taskId],
    queryFn: async () => {
      const response = await axios.get(`/api/tasks/${taskId}/daily-hours`);
      return response.data;
    },
    enabled: !!taskId,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

// Mutations for all CRUD operations
export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data) => {
      const response = await axios.put('/api/profile', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userDetail'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};

export const useUpdateSystemSettings = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data) => {
      const response = await axios.put('/api/system-settings/', data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['systemSettings'] });
      if (data) {
        import('../constants/systemSettingsDefaults').then(({ mergeSystemSettings }) => {
          queryClient.setQueryData(['systemSettings'], mergeSystemSettings(data));
        });
      }
    },
  });
};

export const useAddTaskComment = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ taskId, comment }) => {
      const response = await axios.post(`/api/tasks/${taskId}/comments`, { comment });
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['taskActivity', variables.taskId] });
      queryClient.invalidateQueries({ queryKey: ['taskDetail', variables.taskId] });
    },
  });
};

export const useUpdateTaskStatus = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ taskId, status }) => {
      const response = await axios.patch(`/api/tasks/${taskId}/status`, { status });
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['assignedTasks'] });
      queryClient.invalidateQueries({ queryKey: ['taskDetail', variables.taskId] });
    },
  });
};

export const useAddTaskNote = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ taskId, note }) => {
      const response = await axios.post(`/api/tasks/${taskId}/notes`, { note });
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['taskNotes', variables.taskId] });
    },
  });
};

export const useCreateSubtask = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ taskId, subtask }) => {
      const response = await axios.post(`/api/tasks/${taskId}/subtasks`, subtask);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['taskSubtasks', variables.taskId] });
    },
  });
};

export const useUpdateSubtask = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ taskId, subtaskId, data }) => {
      const response = await axios.put(`/api/tasks/${taskId}/subtasks/${subtaskId}`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['taskSubtasks', variables.taskId] });
    },
  });
};

export const useDeleteSubtask = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ taskId, subtaskId }) => {
      const response = await axios.delete(`/api/tasks/${taskId}/subtasks/${subtaskId}`);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['taskSubtasks', variables.taskId] });
    },
  });
};

export const useUploadAvatar = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append('avatar', file);
      const response = await axios.post('/api/profile/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userDetail'] });
    },
  });
};

export const useChangePassword = () => {
  return useMutation({
    mutationFn: async (data) => {
      const response = await axios.post('/api/profile/change-password', data);
      return response.data;
    },
  });
};

export const useConnectGitHub = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async () => {
      const response = await axios.post('/api/github/connect');
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['githubConnectionStatus'] });
      queryClient.invalidateQueries({ queryKey: ['integrationStatus'] });
    },
  });
};

export const useDisconnectGitHub = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async () => {
      const response = await axios.post('/api/github/disconnect');
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['githubConnectionStatus'] });
      queryClient.invalidateQueries({ queryKey: ['githubOrganizations'] });
      queryClient.invalidateQueries({ queryKey: ['githubRepositories'] });
      queryClient.invalidateQueries({ queryKey: ['integrationStatus'] });
    },
  });
};

export const useAIValidation = () => {
  return useMutation({
    mutationFn: async (data) => {
      const response = await axios.post('/api/ai-validation/ai-estimate', data);
      return response.data;
    },
  });
};

// Additional hooks for TaskDetail and other components
export const useTaskComments = (taskId) => {
  return useQuery({
    queryKey: ['taskComments', taskId],
    queryFn: async () => {
      const response = await axios.get(`/api/tasks/${taskId}/comments`);
      return response.data;
    },
    enabled: !!taskId,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

export const useNotes = (noteType, relatedId) => {
  return useQuery({
    queryKey: ['notes', noteType, relatedId],
    queryFn: async () => {
      const response = await axios.get(`/api/notes?note_type=${noteType}&related_id=${relatedId}`);
      return response.data;
    },
    enabled: !!noteType && !!relatedId,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

export const useGitHubStatus = () => {
  return useQuery({
    queryKey: ['githubStatus'],
    queryFn: async () => {
      const response = await axios.get('/api/github/status');
      return response.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// Mutations for TaskDetail

export const useUpdateTaskComment = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ taskId, commentId, comment }) => {
      const response = await axios.put(`/api/tasks/${taskId}/comments/${commentId}`, { comment });
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['taskComments', variables.taskId] });
    },
  });
};

export const useDeleteTaskComment = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ taskId, commentId }) => {
      const response = await axios.delete(`/api/tasks/${taskId}/comments/${commentId}`);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['taskComments', variables.taskId] });
      queryClient.invalidateQueries({ queryKey: ['taskDetail', variables.taskId] });
    },
  });
};

export const useAddCommentReaction = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ taskId, commentId, emoji }) => {
      const response = await axios.post(`/api/tasks/${taskId}/comments/${commentId}/reactions`, { emoji });
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['taskComments', variables.taskId] });
    },
  });
};

export const useRemoveCommentReaction = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ taskId, commentId, emoji }) => {
      const response = await axios.delete(`/api/tasks/${taskId}/comments/${commentId}/reactions`, {
        data: { emoji }
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['taskComments', variables.taskId] });
    },
  });
};

export const useUploadScreenshot = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ taskId, file, description }) => {
      const formData = new FormData();
      formData.append('screenshot', file);
      if (description) formData.append('description', description);
      
      const response = await axios.post(`/api/tasks/${taskId}/screenshots`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['taskScreenshots', variables.taskId] });
      queryClient.invalidateQueries({ queryKey: ['taskDetail', variables.taskId] });
    },
  });
};

export const useDeleteScreenshot = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ taskId, screenshotId }) => {
      const response = await axios.delete(`/api/tasks/${taskId}/screenshots/${screenshotId}`);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['taskScreenshots', variables.taskId] });
      queryClient.invalidateQueries({ queryKey: ['taskDetail', variables.taskId] });
    },
  });
};

export const useCreateNote = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (noteData) => {
      const response = await axios.post('/api/notes', noteData);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['notes', variables.note_type, variables.related_id] });
    },
  });
};

export const useUpdateNote = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ noteId, content, visibility }) => {
      const response = await axios.put(`/api/notes/${noteId}`, { content, visibility });
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['notes'] });
    },
  });
};

export const useDeleteNote = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (noteId) => {
      const response = await axios.delete(`/api/notes/${noteId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notes'] });
    },
  });
};

export const useCreateGitHubCommit = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ taskId, message }) => {
      const response = await axios.post(`/api/tasks/${taskId}/github-commit`, { message });
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['taskDetail', variables.taskId] });
    },
  });
};

export const useCreateGitHubPR = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (taskId) => {
      const response = await axios.post(`/api/tasks/${taskId}/github-create-pr`);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['taskDetail', variables.taskId] });
    },
  });
};

export const useConnectTaskToGitHub = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ taskId, githubConfig }) => {
      const response = await axios.post(`/api/tasks/${taskId}/github-connect`, githubConfig);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['taskDetail', variables.taskId] });
      queryClient.invalidateQueries({ queryKey: ['githubConnectionStatus'] });
    },
  });
};

export const useDisconnectTaskFromGitHub = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (taskId) => {
      const response = await axios.delete(`/api/tasks/${taskId}/github-disconnect`);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['taskDetail', variables.taskId] });
      queryClient.invalidateQueries({ queryKey: ['githubConnectionStatus'] });
    },
  });
};

export const useAddGitHubCollaborator = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ taskId, username }) => {
      const response = await axios.post(`/api/tasks/${taskId}/github-collaborators`, { username });
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['taskDetail', variables.taskId] });
    },
  });
};

export const useRemoveGitHubCollaborator = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ taskId, username }) => {
      const response = await axios.delete(`/api/tasks/${taskId}/github-collaborators/${username}`);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['taskDetail', variables.taskId] });
    },
  });
};

export const useCreateGitHubRepository = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (repoName) => {
      const response = await axios.post('/api/github/create-repository', { name: repoName });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['githubRepositories'] });
    },
  });
};

// Team management hooks
export const useTeamMembers = (options = {}) => {
  return useQuery({
    queryKey: ['teamMembers'],
    queryFn: async () => {
      const response = await axios.get('/api/team');
      return response.data;
    },
    staleTime: 30 * 1000, // 30s — refresh promptly after role/approval changes
    enabled: options.enabled !== false,
  });
};

export const usePendingUsers = (options = {}) => {
  return useQuery({
    queryKey: ['pendingUsers'],
    queryFn: async () => {
      const response = await axios.get('/api/team/pending');
      return response.data;
    },
    staleTime: 30 * 1000, // 30s — refresh promptly after approve/decline
    enabled: options.enabled !== false,
  });
};

export const useDisabledUsers = (options = {}) => {
  return useQuery({
    queryKey: ['disabledUsers'],
    queryFn: async () => {
      const response = await axios.get('/api/team/disabled');
      return response.data;
    },
    staleTime: 30 * 1000, // 30s — refresh promptly after enable/disable
    enabled: options.enabled !== false,
  });
};

export const useInviteUser = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ emails, roles }) => {
      const response = await axios.post('/api/team/invite', { emails, roles });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamMembers'] });
      queryClient.invalidateQueries({ queryKey: ['pendingUsers'] });
      queryClient.invalidateQueries({ queryKey: ['teamActive'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useCreateUser = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (userData) => {
      const response = await axios.post('/api/users', userData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['teamMembers'] });
      queryClient.invalidateQueries({ queryKey: ['teamActive'] });
    },
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ userId, userData }) => {
      const response = await axios.put(`/api/users/${userId}`, userData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['teamMembers'] });
      queryClient.invalidateQueries({ queryKey: ['userDetail'] });
      queryClient.invalidateQueries({ queryKey: ['teamActive'] });
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (userId) => {
      const response = await axios.delete(`/api/users/${userId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['teamMembers'] });
      queryClient.invalidateQueries({ queryKey: ['teamActive'] });
    },
  });
};

export const useUpdateUserRole = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ userId, role }) => {
      const response = await axios.put(`/api/team/${userId}/role`, { role });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamMembers'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['teamActive'] });
    },
  });
};

export const useToggleUserStatus = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ userId, action }) => {
      const response = await axios.post(`/api/team/${userId}/${action}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamMembers'] });
      queryClient.invalidateQueries({ queryKey: ['disabledUsers'] });
      queryClient.invalidateQueries({ queryKey: ['teamActive'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useApproveUser = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ userId, action }) => {
      const response = await axios.post(`/api/team/${userId}/approval`, { action });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pendingUsers'] });
      queryClient.invalidateQueries({ queryKey: ['teamMembers'] });
      queryClient.invalidateQueries({ queryKey: ['disabledUsers'] });
      queryClient.invalidateQueries({ queryKey: ['teamActive'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useReapproveUser = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (userId) => {
      const response = await axios.post(`/api/team/${userId}/reapprove`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamMembers'] });
      queryClient.invalidateQueries({ queryKey: ['pendingUsers'] });
      queryClient.invalidateQueries({ queryKey: ['teamActive'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useResendInvitation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (userId) => {
      const response = await axios.post(`/api/team/resend-invitation/${userId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamMembers'] });
    },
  });
};

export const useResendVerification = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (userId) => {
      const response = await axios.post(`/api/team/${userId}/resend-verification`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamMembers'] });
    },
  });
};

// Project Member Management Hooks
export const useAddProjectMember = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ projectId, memberId }) => {
      const response = await axios.post(`/api/projects/${projectId}/add-member/${memberId}`);
      return response.data;
    },
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ['projectDetail', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
};

export const useRemoveProjectMember = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ projectId, memberId }) => {
      const response = await axios.post(`/api/projects/${projectId}/remove-member/${memberId}`);
      return response.data;
    },
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ['projectDetail', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
};

// ----- Share management (authenticated) -----
export const useProjectShares = (projectId) => {
  return useQuery({
    queryKey: ['projectShares', projectId],
    queryFn: async () => {
      const response = await axios.get(`/api/shares/project/${projectId}`);
      return response.data;
    },
    enabled: !!projectId,
  });
};

export const useCreateShare = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await axios.post('/api/shares', data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projectShares', variables.project_id] });
    },
  });
};

export const useUpdateShare = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ shareId, data }) => {
      const response = await axios.put(`/api/shares/${shareId}`, data);
      return response.data;
    },
    onSuccess: (_, { shareId }) => {
      queryClient.invalidateQueries({ queryKey: ['projectShares'] });
    },
  });
};

export const useDeleteShare = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (shareId) => {
      await axios.delete(`/api/shares/${shareId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projectShares'] });
    },
  });
};

export const useRegenerateShareToken = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (shareId) => {
      const response = await axios.post(`/api/shares/${shareId}/regenerate`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projectShares'] });
    },
  });
};

// ----- Public share (no auth; token + optional password) -----
const publicShareHeaders = (password) => {
  const h = {};
  if (password) h['X-Share-Password'] = password;
  return h;
};

export const usePublicShareOverview = (token, password, enabled = true) => {
  return useQuery({
    queryKey: ['publicShareOverview', token, !!password],
    queryFn: async () => {
      const response = await publicShareApi.get(`/api/public/share/${token}`, { headers: publicShareHeaders(password) });
      return response.data;
    },
    enabled: !!token && enabled,
    retry: (failureCount, error) => error?.response?.status === 401 && failureCount < 1 ? true : false,
  });
};

export const usePublicShareBoard = (token, password, enabled = true) => {
  return useQuery({
    queryKey: ['publicShareBoard', token],
    queryFn: async () => {
      const response = await publicShareApi.get(`/api/public/share/${token}/board`, { headers: publicShareHeaders(password) });
      return response.data;
    },
    enabled: !!token && enabled,
  });
};

export const usePublicShareAnalytics = (token, password, enabled = true) => {
  return useQuery({
    queryKey: ['publicShareAnalytics', token],
    queryFn: async () => {
      const response = await publicShareApi.get(`/api/public/share/${token}/analytics`, { headers: publicShareHeaders(password) });
      return response.data;
    },
    enabled: !!token && enabled,
  });
};

export const usePublicShareTeam = (token, password, enabled = true) => {
  return useQuery({
    queryKey: ['publicShareTeam', token],
    queryFn: async () => {
      const response = await publicShareApi.get(`/api/public/share/${token}/team`, { headers: publicShareHeaders(password) });
      return response.data;
    },
    enabled: !!token && enabled,
  });
};

export const useVerifySharePassword = () => {
  return useMutation({
    mutationFn: async ({ token, password }) => {
      const response = await publicShareApi.post(`/api/public/share/${token}/verify`, { password });
      return response.data;
    },
  });
};

// ==================== Platform Chat ====================

export const useIncomingChatRequests = () => {
  return useQuery({
    queryKey: ['incomingChatRequests'],
    queryFn: async () => {
      const response = await axios.get('/api/chat-requests/incoming');
      return response.data;
    },
    staleTime: 30 * 1000,
  });
};

export const useOutgoingChatRequests = () => {
  return useQuery({
    queryKey: ['outgoingChatRequests'],
    queryFn: async () => {
      const response = await axios.get('/api/chat-requests/outgoing');
      return response.data;
    },
    staleTime: 30 * 1000,
  });
};

export const useSendChatRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ to_user_id, message }) => {
      const response = await axios.post('/api/chat-requests', { to_user_id, message });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['outgoingChatRequests'] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
};

export const useAcceptChatRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (requestId) => {
      const response = await axios.post(`/api/chat-requests/${requestId}/accept`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incomingChatRequests'] });
      queryClient.invalidateQueries({ queryKey: ['outgoingChatRequests'] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      queryClient.invalidateQueries({ queryKey: ['chatUnreadCount'] });
    },
  });
};

export const useDeclineChatRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (requestId) => {
      const response = await axios.post(`/api/chat-requests/${requestId}/decline`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incomingChatRequests'] });
      queryClient.invalidateQueries({ queryKey: ['outgoingChatRequests'] });
    },
  });
};

export const useConversations = () => {
  return useQuery({
    queryKey: ['conversations'],
    queryFn: async () => {
      const response = await axios.get('/api/conversations');
      const data = response.data;
      try {
        const { prefetchAvatars } = await import('../utils/avatarUtils');
        const blobs = data
          .map((c) => c.other_user?.avatar)
          .filter(Boolean);
        prefetchAvatars(blobs);
      } catch (_) {
        /* non-critical */
      }
      return data;
    },
    staleTime: 30 * 1000,
  });
};

function isOptimisticMessage(m) {
  return !!m?.pending || String(m?.id || '').startsWith('temp-');
}

function matchesOptimisticDuplicate(temp, confirmed) {
  if (!isOptimisticMessage(temp) || !confirmed) return false;
  return (
    temp.sender_id === confirmed.sender_id &&
    (temp.type || 'text') === (confirmed.type || 'text') &&
    (temp.content || '') === (confirmed.content || '') &&
    (temp.image_url || '') === (confirmed.image_url || '')
  );
}

function mergeMessageList(messages = [], message, replaceTempId) {
  let list = messages.filter((m) => {
    if (replaceTempId && m.id === replaceTempId) return false;
    if (!isOptimisticMessage(message) && matchesOptimisticDuplicate(m, message)) {
      return false;
    }
    return true;
  });
  const idx = list.findIndex((m) => m.id === message.id);
  if (idx >= 0) {
    list[idx] = message;
    return list;
  }
  return [...list, message];
}

/** Merge a message into conversation cache; replaces optimistic temps when confirmed. */
export function appendMessageToConversationCache(old, message, replaceTempId) {
  if (old?.pages) {
    if (!old.pages.length) {
      return {
        pages: [{ messages: [message], has_more: false, next_cursor: null }],
        pageParams: old.pageParams || [undefined],
      };
    }
    // Scrub temps from all pages; append/replace on newest page (pages[0])
    const pages = old.pages.map((page, i) => {
      if (i === 0) {
        return {
          ...page,
          messages: mergeMessageList(page.messages, message, replaceTempId),
        };
      }
      return {
        ...page,
        messages: (page.messages || []).filter((m) => {
          if (replaceTempId && m.id === replaceTempId) return false;
          if (m.id === message.id) return false;
          if (!isOptimisticMessage(message) && matchesOptimisticDuplicate(m, message)) {
            return false;
          }
          return true;
        }),
      };
    });
    return { ...old, pages };
  }

  if (!old) {
    return {
      pages: [{ messages: [message], has_more: false, next_cursor: null }],
      pageParams: [undefined],
    };
  }
  if (old.messages) {
    return { ...old, messages: mergeMessageList(old.messages, message, replaceTempId) };
  }
  return {
    pages: [{ messages: [message], has_more: false, next_cursor: null }],
    pageParams: [undefined],
  };
}

export const useConversationMessages = (conversationId, options = {}) => {
  return useInfiniteQuery({
    queryKey: ['conversationMessages', conversationId],
    queryFn: async ({ pageParam }) => {
      const params = { limit: 50 };
      if (pageParam) params.cursor = pageParam;
      const response = await axios.get(`/api/conversations/${conversationId}/messages`, {
        params,
      });
      return response.data;
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) =>
      lastPage?.has_more && lastPage?.next_cursor ? lastPage.next_cursor : undefined,
    enabled: !!conversationId && options.enabled !== false,
    staleTime: 10 * 1000,
  });
};

export const useSendMessage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      conversationId,
      content,
      type = 'text',
      image_url,
      mentions,
      metadata,
    }) => {
      const response = await axios.post(`/api/conversations/${conversationId}/messages`, {
        content,
        type,
        image_url,
        mentions,
        metadata,
      });
      return response.data;
    },
    onMutate: async (variables) => {
      const tempId =
        variables.tempId ||
        `temp-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      const key = ['conversationMessages', variables.conversationId];
      await queryClient.cancelQueries({ queryKey: key });
      const previousMessages = queryClient.getQueryData(key);
      const previousConversations = queryClient.getQueryData(['conversations']);
      const now = new Date().toISOString();
      const optimistic = {
        id: tempId,
        conversation_id: variables.conversationId,
        sender_id: variables.senderId,
        type: variables.type || 'text',
        content: variables.content || '',
        mentions: variables.mentions || [],
        image_url: variables.image_url || null,
        metadata: variables.metadata || null,
        read_by: variables.senderId ? [variables.senderId] : [],
        created_at: now,
        edited_at: null,
        is_deleted: false,
        sender_name: variables.senderName || 'You',
        sender_avatar: variables.senderAvatar || null,
        pending: true,
      };
      queryClient.setQueryData(key, (old) =>
        appendMessageToConversationCache(old, optimistic)
      );
      const isGiphy =
        typeof variables.image_url === 'string' &&
        variables.image_url.includes('giphy.com');
      const previewText =
        variables.content ||
        (variables.type === 'voice'
          ? '🎤 Voice note'
          : variables.type === 'spotify'
            ? '🎵 Spotify track'
          : variables.type === 'image'
            ? isGiphy
              ? 'GIF'
              : '📷 Image'
            : '');
      queryClient.setQueryData(['conversations'], (old) => {
        if (!Array.isArray(old)) return old;
        const updated = old.map((c) =>
          c.id === variables.conversationId
            ? {
                ...c,
                updated_at: now,
                last_message: {
                  text: String(previewText || '').slice(0, 200),
                  sender_id: variables.senderId,
                  sent_at: now,
                  type: variables.type || 'text',
                },
              }
            : c
        );
        const idx = updated.findIndex((c) => c.id === variables.conversationId);
        if (idx <= 0) return updated;
        const [row] = updated.splice(idx, 1);
        return [row, ...updated];
      });
      return { previousMessages, previousConversations, tempId };
    },
    onError: (_err, variables, context) => {
      if (context?.previousMessages !== undefined) {
        queryClient.setQueryData(
          ['conversationMessages', variables.conversationId],
          context.previousMessages
        );
      }
      if (context?.previousConversations !== undefined) {
        queryClient.setQueryData(['conversations'], context.previousConversations);
      }
    },
    onSuccess: (data, variables, context) => {
      queryClient.setQueryData(
        ['conversationMessages', variables.conversationId],
        (old) => appendMessageToConversationCache(old, data, context?.tempId)
      );
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
};

export const useEditMessage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ conversationId, messageId, content, mentions }) => {
      const response = await axios.patch(
        `/api/conversations/${conversationId}/messages/${messageId}`,
        { content, mentions }
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['conversationMessages', variables.conversationId],
      });
    },
  });
};

export const useDeleteMessage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ conversationId, messageId }) => {
      const response = await axios.delete(
        `/api/conversations/${conversationId}/messages/${messageId}`
      );
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['conversationMessages', variables.conversationId],
      });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
};

export const useDeleteConversation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (conversationId) => {
      const response = await axios.delete(`/api/conversations/${conversationId}`);
      return response.data;
    },
    onSuccess: (_data, conversationId) => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      queryClient.invalidateQueries({ queryKey: ['chatUnreadCount'] });
      queryClient.removeQueries({ queryKey: ['conversationMessages', conversationId] });
    },
  });
};

/** Re-open a soft-deleted (or prior-accepted) DM without a new chat request. */
export const useReopenConversation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ conversationId, toUserId } = {}) => {
      if (conversationId) {
        const response = await axios.get(`/api/conversations/${conversationId}`);
        return response.data;
      }
      const response = await axios.post('/api/chat-requests', {
        to_user_id: toUserId,
        message: '',
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      queryClient.invalidateQueries({ queryKey: ['chatUserSearch'] });
      queryClient.invalidateQueries({ queryKey: ['chatUnreadCount'] });
    },
  });
};

export const useMarkAsRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (conversationId) => {
      const response = await axios.post(`/api/conversations/${conversationId}/read`);
      return response.data;
    },
    onSuccess: (_data, conversationId) => {
      queryClient.setQueryData(['conversations'], (old) => {
        if (!Array.isArray(old)) return old;
        return old.map((c) =>
          c.id === conversationId ? { ...c, unread_count: 0 } : c
        );
      });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      queryClient.invalidateQueries({ queryKey: ['chatUnreadCount'] });
    },
  });
};

export const usePinConversation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ conversationId, pinned }) => {
      if (pinned) {
        const response = await axios.post(`/api/conversations/${conversationId}/pin`);
        return response.data;
      }
      const response = await axios.delete(`/api/conversations/${conversationId}/pin`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
};

export const usePinMessage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ conversationId, messageId, pinned }) => {
      if (pinned) {
        const response = await axios.post(
          `/api/conversations/${conversationId}/messages/${messageId}/pin`
        );
        return response.data;
      }
      const response = await axios.delete(
        `/api/conversations/${conversationId}/messages/${messageId}/pin`
      );
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      queryClient.invalidateQueries({
        queryKey: ['conversationMessages', variables.conversationId],
      });
    },
  });
};

export const useCreateGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, description = '', member_ids = [] }) => {
      const response = await axios.post('/api/conversations/groups', {
        name,
        description: description || '',
        member_ids,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      queryClient.invalidateQueries({ queryKey: ['groupInvites'] });
    },
  });
};

export const useGroupInvites = (direction = 'incoming') => {
  return useQuery({
    queryKey: ['groupInvites', direction],
    queryFn: async () => {
      const response = await axios.get(`/api/conversations/group-invites/${direction}`);
      return response.data;
    },
    staleTime: 15 * 1000,
  });
};

export const useAcceptGroupInvite = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (inviteId) => {
      const response = await axios.post(
        `/api/conversations/group-invites/${inviteId}/accept`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groupInvites'] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
};

export const useDeclineGroupInvite = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (inviteId) => {
      const response = await axios.post(
        `/api/conversations/group-invites/${inviteId}/decline`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groupInvites'] });
    },
  });
};

export const useInviteToGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ conversationId, user_ids }) => {
      const response = await axios.post(
        `/api/conversations/${conversationId}/invites`,
        { user_ids }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groupInvites'] });
    },
  });
};

export const useLeaveGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (conversationId) => {
      const response = await axios.post(`/api/conversations/${conversationId}/leave`);
      return response.data;
    },
    onSuccess: (_data, conversationId) => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      queryClient.removeQueries({ queryKey: ['conversationMessages', conversationId] });
    },
  });
};

export const useDeleteGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (conversationId) => {
      const response = await axios.delete(`/api/conversations/${conversationId}/group`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
};

export const useUploadChatImage = () => {
  return useMutation({
    mutationFn: async ({ conversationId, file }) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(
        `/api/conversations/${conversationId}/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    },
  });
};

export const useUnreadCount = () => {
  return useQuery({
    queryKey: ['chatUnreadCount'],
    queryFn: async () => {
      const response = await axios.get('/api/conversations/unread-count');
      return response.data;
    },
    staleTime: 15 * 1000,
  });
};

export const useMentionSearch = (type, query, enabled = true, conversationId = null) => {
  return useQuery({
    queryKey: ['mentionSearch', type, query, conversationId],
    queryFn: async () => {
      const response = await axios.get('/api/conversations/mention-search', {
        params: {
          type,
          q: query || '',
          ...(conversationId ? { conversation_id: conversationId } : {}),
        },
      });
      return response.data;
    },
    enabled: !!type && enabled,
    staleTime: 30 * 1000,
  });
};

export const useGiphySearch = (query = '', enabled = true) => {
  return useQuery({
    queryKey: ['giphySearch', query],
    queryFn: async () => {
      const response = await axios.get('/api/conversations/giphy', {
        params: { q: query || '', limit: 24 },
      });
      return response.data;
    },
    enabled,
    staleTime: 60 * 1000,
  });
};

/**
 * Org directory for New chat / New group.
 * Fetches once (empty q), then callers filter client-side so typing feels instant.
 */
export const useChatUserSearch = (enabled = true) => {
  return useQuery({
    queryKey: ['chatUserSearch', 'directory'],
    queryFn: async () => {
      const response = await axios.get('/api/conversations/search-users', {
        params: { q: '', limit: 200 },
      });
      const data = response.data || [];
      try {
        const { prefetchAvatars } = await import('../utils/avatarUtils');
        prefetchAvatars(data.map((u) => u.avatar).filter(Boolean));
      } catch (_) {
        /* non-critical */
      }
      return data;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
};

/** Admin: Message Logs PIN length / configured flag (never returns the PIN) */
export const useAdminMessageLogsPinMeta = (enabled = true) => {
  return useQuery({
    queryKey: ['adminMessageLogsPinMeta'],
    queryFn: async () => {
      const response = await axios.get('/api/conversations/admin/pin-meta');
      return response.data;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
};

/** Admin: verify Message Logs PIN against server Key Vault / env */
export const useUnlockAdminMessageLogs = () => {
  return useMutation({
    mutationFn: async (pin) => {
      const response = await axios.post('/api/conversations/admin/unlock', { pin });
      return response.data;
    },
  });
};

/** Admin: platform DM/group conversation list for Message Logs */
export const useAdminConversations = (filters = {}, enabled = true) => {
  return useQuery({
    queryKey: ['adminConversations', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters || {}).forEach(([key, value]) => {
        if (value !== '' && value != null) {
          params.append(key, String(value));
        }
      });
      const response = await axios.get(`/api/conversations/admin?${params.toString()}`);
      return response.data;
    },
    enabled,
    staleTime: 30 * 1000,
  });
};

/** Admin: Message Logs header stats */
export const useAdminConversationStats = (enabled = true) => {
  return useQuery({
    queryKey: ['adminConversationStats'],
    queryFn: async () => {
      const response = await axios.get('/api/conversations/admin/stats');
      return response.data;
    },
    enabled,
    staleTime: 60 * 1000,
  });
};

/** Admin: conversation header for transcript modal */
export const useAdminConversationDetail = (conversationId, enabled = true) => {
  return useQuery({
    queryKey: ['adminConversationDetail', conversationId],
    queryFn: async () => {
      const response = await axios.get(`/api/conversations/admin/${conversationId}`);
      return response.data;
    },
    enabled: !!conversationId && enabled,
    staleTime: 30 * 1000,
  });
};

/** Admin: paginated transcript including soft-deleted messages */
export const useAdminConversationMessages = (conversationId, enabled = true) => {
  return useQuery({
    queryKey: ['adminConversationMessages', conversationId],
    queryFn: async () => {
      const response = await axios.get(
        `/api/conversations/admin/${conversationId}/messages`,
        { params: { limit: 100 } }
      );
      return response.data;
    },
    enabled: !!conversationId && enabled,
    staleTime: 15 * 1000,
  });
};

/** Case-insensitive filter over a cached directory (no network). */
export function filterChatDirectory(users = [], query = '') {
  const term = (query || '').trim().toLowerCase();
  if (!term) return users;
  return users.filter((u) => {
    const name = (u.full_name || '').toLowerCase();
    const username = (u.username || '').toLowerCase();
    return name.includes(term) || username.includes(term);
  });
}

// ---------------------------------------------------------------------------
// Announcements & Inbox
// ---------------------------------------------------------------------------

export const useActiveBanners = () => {
  return useQuery({
    queryKey: ['activeBanners'],
    queryFn: async () => {
      const response = await axios.get('/api/announcements/active');
      return response.data || [];
    },
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });
};

export const useAdminAnnouncements = (filters = {}) => {
  return useQuery({
    queryKey: ['adminAnnouncements', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters || {}).forEach(([key, value]) => {
        if (value !== '' && value != null && value !== false) {
          params.append(key, String(value));
        }
      });
      const response = await axios.get(`/api/announcements/admin?${params}`);
      return response.data;
    },
    staleTime: 30 * 1000,
  });
};

export const useCreateAnnouncement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const response = await axios.post('/api/announcements/', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminAnnouncements'] });
      queryClient.invalidateQueries({ queryKey: ['activeBanners'] });
      queryClient.invalidateQueries({ queryKey: ['inboxItems'] });
      queryClient.invalidateQueries({ queryKey: ['inboxUnreadCount'] });
    },
  });
};

export const useUpdateAnnouncement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }) => {
      const response = await axios.put(`/api/announcements/${id}`, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminAnnouncements'] });
      queryClient.invalidateQueries({ queryKey: ['activeBanners'] });
      queryClient.invalidateQueries({ queryKey: ['inboxItems'] });
      queryClient.invalidateQueries({ queryKey: ['inboxUnreadCount'] });
    },
  });
};

export const useDeleteAnnouncement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const response = await axios.delete(`/api/announcements/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminAnnouncements'] });
      queryClient.invalidateQueries({ queryKey: ['activeBanners'] });
      queryClient.invalidateQueries({ queryKey: ['inboxItems'] });
      queryClient.invalidateQueries({ queryKey: ['inboxUnreadCount'] });
    },
  });
};

export const useToggleAnnouncement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, active }) => {
      const response = await axios.post(`/api/announcements/${id}/toggle`, { active });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminAnnouncements'] });
      queryClient.invalidateQueries({ queryKey: ['activeBanners'] });
      queryClient.invalidateQueries({ queryKey: ['inboxUnreadCount'] });
    },
  });
};

export const useArchiveAnnouncement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const response = await axios.post(`/api/announcements/${id}/archive`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminAnnouncements'] });
      queryClient.invalidateQueries({ queryKey: ['activeBanners'] });
      queryClient.invalidateQueries({ queryKey: ['inboxUnreadCount'] });
    },
  });
};

export const useDismissAnnouncement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const response = await axios.post(`/api/announcements/${id}/dismiss`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activeBanners'] });
      queryClient.invalidateQueries({ queryKey: ['inboxItems'] });
      queryClient.invalidateQueries({ queryKey: ['inboxUnreadCount'] });
    },
  });
};

export const useMarkAnnouncementRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const response = await axios.post(`/api/announcements/${id}/read`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activeBanners'] });
      queryClient.invalidateQueries({ queryKey: ['inboxItems'] });
      queryClient.invalidateQueries({ queryKey: ['inboxUnreadCount'] });
    },
  });
};

export const useInboxItems = (filters = {}) => {
  return useQuery({
    queryKey: ['inboxItems', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters || {}).forEach(([key, value]) => {
        if (value !== '' && value != null && value !== false) {
          params.append(key, String(value));
        }
      });
      const response = await axios.get(`/api/notifications/inbox?${params}`);
      return response.data;
    },
    staleTime: 20 * 1000,
    placeholderData: (previousData) => previousData,
  });
};

export const useInboxUnreadCount = () => {
  return useQuery({
    queryKey: ['inboxUnreadCount'],
    queryFn: async () => {
      const response = await axios.get('/api/notifications/inbox/unread-count');
      return response.data;
    },
    staleTime: 15 * 1000,
    refetchInterval: 60 * 1000,
  });
};

export const useSendPersonalNotification = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const response = await axios.post('/api/notifications/personal', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inboxItems'] });
      queryClient.invalidateQueries({ queryKey: ['inboxUnreadCount'] });
      queryClient.invalidateQueries({ queryKey: ['activeBanners'] });
    },
  });
};

export const useMarkPersonalNotificationRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const response = await axios.put(`/api/notifications/${id}/read`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inboxItems'] });
      queryClient.invalidateQueries({ queryKey: ['inboxUnreadCount'] });
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
};

// ---------------------------------------------------------------------------
// Reports hooks
// ---------------------------------------------------------------------------

export const useReportTemplates = () => {
  return useQuery({
    queryKey: ['reportTemplates'],
    queryFn: async () => {
      const response = await axios.get('/api/reports/templates');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
  });
};

export const useScheduledReports = () => {
  return useQuery({
    queryKey: ['scheduledReports'],
    queryFn: async () => {
      const response = await axios.get('/api/reports/scheduled');
      return response.data;
    },
    staleTime: 30 * 1000,
  });
};

export const useReportBenchmarks = () => {
  return useQuery({
    queryKey: ['reportBenchmarks'],
    queryFn: async () => {
      const response = await axios.get('/api/reports/benchmarks');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateReportSchedule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const response = await axios.post('/api/reports/schedule', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduledReports'] });
    },
  });
};

export const useCancelReportSchedule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (scheduleId) => {
      const response = await axios.delete(`/api/reports/scheduled/${scheduleId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduledReports'] });
    },
  });
};

// Alias for useTeamActive — same endpoint and cache key to avoid duplicate fetches
export const useActiveTeamMembers = (options = {}) => useTeamActive(options);